import type { ReactNode } from "react";
import { Btn, Icon } from "@v0y4ge/jrni-ui";
import { EmptyState } from "../../shell/composites";
import { useT } from "../../lib/i18n";

/**
 * EventsSectionState — the ONE loading / error / empty presentation shared by
 * every screen on the Events rail (Events, Archive, Templates, Ticket Types,
 * Ticket Groups, Landing Pages, Event Hubs). Before this, each screen rolled
 * its own "No X" block with a different icon, tone and button, so the section
 * looked like seven products. Screens now describe their state; the look is
 * owned here.
 *
 * Usage: render it INSTEAD of the list when it returns non-null.
 *   const state = <EventsSectionState loading={loading} error={error} … />;
 *   {state ?? <TheList … />}
 */
export type EventsSectionStateProps = {
  /** Plural, lower-case noun for this screen: "events", "templates", "ticket types". */
  noun: string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** True when the list is empty. */
  empty?: boolean;
  /** True when filters/search are narrowing the list (changes the empty copy). */
  filtered?: boolean;
  onClearFilters?: () => void;
  /** Icon for the true-empty state (defaults to a calendar). */
  icon?: ReactNode;
  /** Primary create action, shown only on the true-empty state. */
  action?: ReactNode;
  /** Optional override for the true-empty body copy. */
  emptyBody?: ReactNode;
};

export function EventsSectionState({
  noun, loading, error, onRetry, empty, filtered, onClearFilters, icon, action, emptyBody,
}: EventsSectionStateProps) {
  const t = useT();

  if (loading) {
    return (
      <EmptyState
        variant="card"
        icon={<Icon.clock size={20} />}
        title={`${t("events.sectionState.loading", "Loading")} ${noun}…`}
      />
    );
  }

  if (error) {
    return (
      <EmptyState
        variant="card"
        tone="danger"
        icon={<Icon.warn size={20} />}
        title={`${t("events.sectionState.couldntLoad", "Couldn't load")} ${noun}`}
        body={error}
        action={onRetry ? <Btn variant="secondary" onClick={onRetry}>{t("common.actions.retry", "Retry")}</Btn> : null}
      />
    );
  }

  if (!empty) return null;

  if (filtered) {
    return (
      <EmptyState
        variant="card"
        icon={<Icon.search size={20} />}
        title={`${t("events.sectionState.noMatchPrefix", "No")} ${noun} ${t("events.sectionState.noMatchSuffix", "match these filters")}`}
        body={t("events.sectionState.tryWideningOrClearingAFilterToSeeMore", "Try widening your search or clearing a filter to see more.")}
        action={onClearFilters ? <Btn variant="secondary" onClick={onClearFilters}>{t("common.actions.clearFilters", "Clear filters")}</Btn> : null}
      />
    );
  }

  return (
    <EmptyState
      variant="card"
      icon={icon ?? <Icon.calendar size={20} />}
      title={`${t("events.sectionState.noYetPrefix", "No")} ${noun} ${t("events.sectionState.noYetSuffix", "yet")}`}
      body={emptyBody ?? `${t("events.sectionState.onceYouCreate", "Once you create")} ${noun}, ${t("events.sectionState.theyllShowUpHere", "they'll show up here.")}`}
      action={action ?? null}
    />
  );
}
